import classNames from "classnames";
import { memo, useEffect, useRef, useState, type FC, type RefObject } from "react";
import { Controller, useFormContext, useFormState, useWatch, type Validate } from "react-hook-form";
import FormFieldsContainer from "../../../components/ui/FormFieldsContainer";
import { FormField, Select } from "@rothoblaas/design-system";
import { useReadOnly } from "../../../components/ui/ReadOnlyGate";
import useI18n from "../../../hooks/useTranslations";
import { LIVE_LOAD_OPTIONS } from "../../../http/deckDesigner/formulas";
import { L_SHAPE, RECTANGULAR_SHAPE } from "../../../http/deckDesigner/types/configurations";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import { clampToMax, getDimensionBounds, type DimensionBoundsInput } from "./dimensionBounds";
import { useMeasurementFormat } from "./measurementFormat";
import NumericField from "./NumericField";
import styles from "./ShapeAndSizeCard.module.css";
import SlopeDirectionModal from "./SlopeDirectionModal";
import { calculateMaxSlopeRiseMm, calculateValidSupportRange, ceilToDecimals, floorToDecimals } from "./supportHeight";

const SHAPE_OPTIONS = [
  {
    id: L_SHAPE,
    labelKey: "lShaped",
    image: "/deckDesigner/Lshaped.png",
  },
  {
    id: RECTANGULAR_SHAPE,
    labelKey: RECTANGULAR_SHAPE,
    image: "/deckDesigner/rectangular.png",
  },
] as const;

// totalHeight only accepts 2 decimals (see its NumericField's decimalScale) - showing more in an
// error message is just noise. Rounds outward (min up, max down) so the displayed bound always
// stays inside the true valid range, never suggesting a value that's actually still out of bounds.
const TOTAL_HEIGHT_DECIMAL_SCALE = 2;

type ShapeId = (typeof SHAPE_OPTIONS)[number]["id"];
type LiveLoadOption = (typeof LIVE_LOAD_OPTIONS)[number];

export type SecondaryDimensionsCache = {
  secondaryBase: number | null;
  secondaryHeight: number | null;
};

type ShapeAndSizeCardProps = {
  accentColor: string;
  // Shared with DeckDesigner so clearing the drawing (DeckDrawingCard's trash button) can drop
  // this cache too, instead of only DeckDesigner's own reset outliving it.
  cachedSecondaryDimensionsRef: RefObject<SecondaryDimensionsCache>;
  defaultValues: DeckDesignerConfigurationFormValues;
};

const ERROR_FIELD_NAMES = [
  "shape",
  "base",
  "height",
  "secondaryBase",
  "secondaryHeight",
  "totalHeight",
  "slopeDegrees",
  "liveLoad",
] as const;

const ShapeAndSizeCard: FC<ShapeAndSizeCardProps> = ({ accentColor, cachedSecondaryDimensionsRef, defaultValues }) => {
  const i18n = useI18n();
  const isReadOnly = useReadOnly();
  const measurementFormat = useMeasurementFormat();
  const calculationTranslation = i18n.translation.deckDesignerModule.configurations.calculation;

  const { control, setValue, getValues, trigger } = useFormContext<DeckDesignerConfigurationFormValues>();
  // Scoped to this card's own fields: reading formState from the context subscribes the whole
  // form (useForm) instead, re-rendering every card on any validation.
  const { errors } = useFormState({ control, name: ERROR_FIELD_NAMES });

  const shape = useWatch({ control, name: "shape", exact: true });
  const base = useWatch({ control, name: "base", exact: true });
  const height = useWatch({ control, name: "height", exact: true });
  const secondaryBase = useWatch({ control, name: "secondaryBase", exact: true });
  const secondaryHeight = useWatch({ control, name: "secondaryHeight", exact: true });
  const lShapeCutoutCorner = useWatch({ control, name: "lShapeCutoutCorner", exact: true });
  const slopeDegrees = useWatch({ control, name: "slopeDegrees", exact: true });
  const slopeArrow = useWatch({ control, name: "slopeArrow", exact: true });
  const boardThickness = useWatch({ control, name: "boardThickness", exact: true });
  const substructure = useWatch({ control, name: "substructure", exact: true });
  const secondaryItem = useWatch({ control, name: "secondaryItem", exact: true });
  const support = useWatch({ control, name: "support", exact: true });
  const connector = useWatch({ control, name: "connector", exact: true });
  const previousShapeRef = useRef(shape);
  const [isSlopeModalOpen, setIsSlopeModalOpen] = useState(false);

  useEffect(() => {
    trigger("totalHeight");
  }, [
    base,
    boardThickness,
    connector,
    height,
    lShapeCutoutCorner,
    secondaryBase,
    secondaryHeight,
    secondaryItem,
    slopeDegrees,
    slopeArrow,
    substructure,
    support,
    trigger,
  ]);

  useEffect(() => {
    if (shape == null) {
      setValue("shape", L_SHAPE);
    }
  }, [shape]);

  useEffect(() => {
    const previousShape = previousShapeRef.current === RECTANGULAR_SHAPE ? RECTANGULAR_SHAPE : L_SHAPE;
    const currentShape = shape === RECTANGULAR_SHAPE ? RECTANGULAR_SHAPE : L_SHAPE;
    const didShapeChange = previousShape !== currentShape;
    previousShapeRef.current = shape;

    if (didShapeChange) {
      if (currentShape === RECTANGULAR_SHAPE) {
        const currentSecondaryBase = getValues("secondaryBase");
        const currentSecondaryHeight = getValues("secondaryHeight");

        cachedSecondaryDimensionsRef.current = {
          secondaryBase: currentSecondaryBase ?? cachedSecondaryDimensionsRef.current.secondaryBase,
          secondaryHeight: currentSecondaryHeight ?? cachedSecondaryDimensionsRef.current.secondaryHeight,
        };

        // Only the ones still set: clearing an already empty one would just re-validate it.
        if (currentSecondaryBase != null) {
          setValue("secondaryBase", null, { shouldDirty: true, shouldValidate: true });
        }
        if (currentSecondaryHeight != null) {
          setValue("secondaryHeight", null, { shouldDirty: true, shouldValidate: true });
        }
      } else {
        const restoreBoundsInput: DimensionBoundsInput = {
          base,
          height,
          secondaryBase: null,
          secondaryHeight: null,
        };
        const restoredSecondaryBase = clampToMax(
          cachedSecondaryDimensionsRef.current.secondaryBase,
          getDimensionBounds("secondaryBase", restoreBoundsInput).max ?? null,
        );
        const restoredSecondaryHeight = clampToMax(
          cachedSecondaryDimensionsRef.current.secondaryHeight,
          getDimensionBounds("secondaryHeight", restoreBoundsInput).max ?? null,
        );

        cachedSecondaryDimensionsRef.current = {
          secondaryBase: restoredSecondaryBase,
          secondaryHeight: restoredSecondaryHeight,
        };

        setValue("secondaryBase", restoredSecondaryBase, { shouldDirty: true, shouldValidate: true });
        setValue("secondaryHeight", restoredSecondaryHeight, { shouldDirty: true, shouldValidate: true });
      }
    }
  }, [base, height, shape]);

  const selectedShape: ShapeId = shape === RECTANGULAR_SHAPE ? RECTANGULAR_SHAPE : L_SHAPE;
  const isRectangularShape = selectedShape === RECTANGULAR_SHAPE;
  const dimensionBoundsInput: DimensionBoundsInput = {
    base,
    height,
    secondaryBase,
    secondaryHeight,
  };
  const baseBounds = getDimensionBounds("base", dimensionBoundsInput);
  const heightBounds = getDimensionBounds("height", dimensionBoundsInput);
  const secondaryBaseBounds = getDimensionBounds("secondaryBase", dimensionBoundsInput);
  const secondaryHeightBounds = getDimensionBounds("secondaryHeight", dimensionBoundsInput);

  // Every piece of the stack (board thickness, connector thickness, secondary item, substructure,
  // support) may still be unset - calculateValidSupportRange already treats each missing one as
  // contributing 0, matching the server's own calculation, so this always recomputes against
  // whatever is currently known instead of waiting for the full build-up to be picked.
  const validateTotalHeight: Validate<number | null, DeckDesignerConfigurationFormValues> = (value, formValues) => {
    const { boardThickness, connector, secondaryItem, substructure, support } = formValues;

    if (value == null) {
      return true;
    }

    const { minimumDeckHeightMm, maximumDeckHeightMm, totalHeightMm } = calculateValidSupportRange(
      substructure,
      boardThickness,
      support,
      secondaryItem,
      connector,
      value,
    );

    if (totalHeightMm == null) {
      return true;
    }

    // A sloped terrain raises the minimum further still: the deck's highest terrain point needs the
    // support to be shorter by this much, so H has to be at least that much taller to begin with.
    // Reduces to the flat minimumDeckHeightMm when there's no slope.
    const slopeAdjustedMinimumDeckHeightMm = minimumDeckHeightMm + calculateMaxSlopeRiseMm(formValues, formValues);

    const formatCm = (valueCm: number) =>
      measurementFormat.length(valueCm, "cm", `${valueCm} ${i18n.translation.common.unitCm}`);

    const minHeightCm = ceilToDecimals(slopeAdjustedMinimumDeckHeightMm / 10, TOTAL_HEIGHT_DECIMAL_SCALE);
    const maxHeightCm = floorToDecimals(maximumDeckHeightMm / 10, TOTAL_HEIGHT_DECIMAL_SCALE);

    if (totalHeightMm < slopeAdjustedMinimumDeckHeightMm) {
      return support != null && connector != null && minimumDeckHeightMm !== maximumDeckHeightMm
        ? calculationTranslation.totalHeightIncompatible(formatCm(minHeightCm), formatCm(maxHeightCm))
        : calculationTranslation.totalHeightTooLittle(formatCm(minHeightCm));
    }

    // The upper bound only applies once a support and a connector are picked - without the support
    // its maxHeight is unknowable, and without the connector the stack is incomplete (ConnectorsCard
    // offers supports that fit with at least one available connector then), so a large total height
    // isn't wrong yet, just not fully checked. Same rule as the server's validateTotalHeight.
    if (support != null && connector != null && totalHeightMm > maximumDeckHeightMm) {
      return calculationTranslation.totalHeightIncompatible(formatCm(minHeightCm), formatCm(maxHeightCm));
    }

    return true;
  };

  return (
    <div>
      <FormFieldsContainer fullWidth>
        <Controller
          name="shape"
          control={control}
          defaultValue={defaultValues.shape}
          render={({ field }) => (
            <FormField label={i18n.translation.common.shape} error={errors.shape}>
              <div className={styles.shapeGrid}>
                {SHAPE_OPTIONS.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={classNames(styles.shapeOption, {
                      [styles.selectedShape]: selectedShape === option.id,
                    })}
                    aria-pressed={selectedShape === option.id}
                    onClick={() => field.onChange(option.id)}
                  >
                    <img
                      className={styles.shapeImage}
                      src={option.image}
                      alt={calculationTranslation[option.labelKey]}
                    />
                    <span className={styles.shapeLabel}>{calculationTranslation[option.labelKey]}</span>
                  </button>
                ))}
              </div>
            </FormField>
          )}
        />

        <NumericField
          name="base"
          metricUnit="m"
          imperialUnit="ft"
          maxDenominator={8}
          label={calculationTranslation.maxBase}
          error={errors.base}
          suffix={" " + i18n.translation.common.unitM}
          defaultValue={defaultValues.base}
          hasShowHelp
          isRectangular={isRectangularShape}
          accentColor={accentColor}
          isImperial={measurementFormat.isImperial}
          minValue={baseBounds.min}
        />
        <NumericField
          name="height"
          metricUnit="m"
          imperialUnit="ft"
          maxDenominator={8}
          label={calculationTranslation.maxHeight}
          error={errors.height}
          suffix={" " + i18n.translation.common.unitM}
          defaultValue={defaultValues.height}
          hasShowHelp
          isRectangular={isRectangularShape}
          accentColor={accentColor}
          isImperial={measurementFormat.isImperial}
          minValue={heightBounds.min}
        />
        {selectedShape !== RECTANGULAR_SHAPE && (
          <>
            <NumericField
              name="secondaryBase"
              metricUnit="m"
              imperialUnit="ft"
              maxDenominator={8}
              label={calculationTranslation.secondaryBase}
              error={errors.secondaryBase}
              suffix={" " + i18n.translation.common.unitM}
              defaultValue={defaultValues.secondaryBase}
              hasShowHelp
              isRectangular={isRectangularShape}
              accentColor={accentColor}
              isImperial={measurementFormat.isImperial}
              minValue={secondaryBaseBounds.min}
              maxValue={secondaryBaseBounds.max}
            />
            <NumericField
              name="secondaryHeight"
              metricUnit="m"
              imperialUnit="ft"
              maxDenominator={8}
              label={calculationTranslation.secondaryHeight}
              error={errors.secondaryHeight}
              suffix={" " + i18n.translation.common.unitM}
              defaultValue={defaultValues.secondaryHeight}
              hasShowHelp
              isRectangular={isRectangularShape}
              accentColor={accentColor}
              isImperial={measurementFormat.isImperial}
              minValue={secondaryHeightBounds.min}
              maxValue={secondaryHeightBounds.max}
            />
          </>
        )}
        <NumericField
          name="totalHeight"
          metricUnit="cm"
          imperialUnit="in"
          label={calculationTranslation.totalHeight}
          error={errors.totalHeight}
          suffix={` ${i18n.translation.common.unitCm}`}
          defaultValue={defaultValues.totalHeight}
          hasShowHelp
          helpImage="/deckDesigner/totalHeight-size.png"
          isRectangular={isRectangularShape}
          accentColor={accentColor}
          isImperial={measurementFormat.isImperial}
          decimalScale={2}
          validate={validateTotalHeight}
        />
        <NumericField
          name="slopeDegrees"
          label={calculationTranslation.slope}
          error={errors.slopeDegrees}
          suffix={` ${i18n.translation.common.unitDeg}`}
          defaultValue={defaultValues.slopeDegrees}
          isRectangular={isRectangularShape}
          accentColor={accentColor}
          isImperial={measurementFormat.isImperial}
          minValue={0}
          maxValue={89}
          onOpen={() => setIsSlopeModalOpen(true)}
        />
        {isSlopeModalOpen && (
          <SlopeDirectionModal
            accentColor={accentColor}
            footprint={{ shape, base, height, secondaryBase, secondaryHeight, lShapeCutoutCorner }}
            terrainSlope={{ slopeDegrees, slopeArrow }}
            onClose={(nextTerrainSlope) => {
              // Closing without changes (or discarding them) writes nothing.
              if (nextTerrainSlope.slopeDegrees !== slopeDegrees) {
                setValue("slopeDegrees", nextTerrainSlope.slopeDegrees, { shouldDirty: true, shouldValidate: true });
              }
              if (nextTerrainSlope.slopeArrow !== slopeArrow) {
                setValue("slopeArrow", nextTerrainSlope.slopeArrow, { shouldDirty: true });
              }
              setIsSlopeModalOpen(false);
            }}
          />
        )}
        <Controller
          name="liveLoad"
          control={control}
          defaultValue={defaultValues.liveLoad}
          rules={{
            validate: (value) => value == null || LIVE_LOAD_OPTIONS.some((option) => option.value === value),
          }}
          render={({ field }) => (
            <FormField label={calculationTranslation.liveLoad} error={errors.liveLoad}>
              {({ labelId, isInvalid }) => (
                <Select<LiveLoadOption, false>
                  isDisabled={isReadOnly}
                  {...field}
                  inputId={labelId}
                  value={LIVE_LOAD_OPTIONS.find((option) => option.value === field.value) ?? null}
                  options={LIVE_LOAD_OPTIONS}
                  getOptionValue={(option) => `${option.value}`}
                  getOptionLabel={(option) =>
                    measurementFormat.liveLoad(option.value, `${option.value} ${i18n.translation.common.unitKnM2}`)
                  }
                  placeholder={`${i18n.translation.common.select}...`}
                  isClearable
                  isInvalid={isInvalid}
                  onChange={(selectedOption) => field.onChange(selectedOption?.value ?? null)}
                />
              )}
            </FormField>
          )}
        />
      </FormFieldsContainer>
    </div>
  );
};

// Memoized: its props are stable, so it re-renders through its own watched fields and props only, not
// whenever its parent does.
export default memo(ShapeAndSizeCard);
